import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { useAuthContext } from './AuthProvider';
import { getAccessiblePaths, refreshMenus } from '../services/menu.service';

interface MenuAccessContextType {
  accessiblePaths: string[];
  isLoading: boolean;
  hasAccess: (path: string) => boolean;
  refreshPaths: () => Promise<void>;
}

const MenuAccessContext = createContext<MenuAccessContextType | undefined>(undefined);

export function MenuAccessProvider({ children }: { children: React.ReactNode }) {
  const { user, isAuthenticated } = useAuthContext();
  const [accessiblePaths, setAccessiblePaths] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  // The user the current paths were loaded for. Only a first load (or a different user) shows
  // the loading state — RouteGuard swaps the page for a spinner while loading, which unmounts it
  // and throws away whatever was typed, so a reload for the same user must happen silently.
  const loadedForUserId = useRef<string | null>(null);
  const userId: string | null = user?.id ?? null;

  const fetchAccessiblePaths = useCallback(async (forceRefresh = false) => {
    if (!isAuthenticated || !userId) {
      loadedForUserId.current = null;
      setAccessiblePaths([]);
      setIsLoading(false);
      return;
    }

    try {
      if (loadedForUserId.current !== userId) setIsLoading(true);

      // Use centralized menu service (fetches once, caches in localStorage)
      const paths = await getAccessiblePaths(forceRefresh);
      setAccessiblePaths(paths);
      loadedForUserId.current = userId;
    } catch (error) {
      console.error('Error loading accessible paths:', error);
      // A failed reload for the same user keeps the paths already loaded.
      if (loadedForUserId.current !== userId) setAccessiblePaths([]);
    } finally {
      setIsLoading(false);
    }
  }, [isAuthenticated, userId]);

  // Fetch paths when the logged-in user changes — not when the auth hook's periodic check
  // (every 15 minutes) hands back a fresh copy of the same user.
  useEffect(() => {
    fetchAccessiblePaths();
  }, [fetchAccessiblePaths]);

  // Check if user has access to a specific path
  const hasAccess = useCallback((path: string): boolean => {
    // Superadmin (default role) has full access
    if (user?.roles?.some((role: any) => role.isDefault === true)) {
      return true;
    }

    // Always allow access to dashboard, pinned, and recent
    const publicPaths = ['/', '/dashboard', '/pinned', '/recent'];
    if (publicPaths.includes(path)) {
      return true;
    }

    // If paths are still loading, deny access (will show loading state)
    if (isLoading) {
      return false;
    }

    // If no accessible paths loaded, deny access
    if (accessiblePaths.length === 0) {
      console.warn('🚫 No accessible paths loaded, denying access to:', path);
      return false;
    }

    // Check exact match first
    if (accessiblePaths.includes(path)) {
      return true;
    }

    const pathParts = path.split('/').filter(Boolean);

    // Common action segments that should inherit access from parent path
    const actionSegments = ['edit', 'create', 'view', 'delete', 'new', 'add', 'update', 'details', 'form', 'branches', 'loan-deduction', 'advance-deduction', 'deduction', 'incentive', 'calculation', 'salary-calculation', 'approve', 'entry', 'checking', 'download-payslip', 'reports', 'main-sheet', 'report'];

    // Check if the path contains action segments or dynamic IDs
    // Examples:
    // /payroll/advance-request/edit/123 -> check /payroll/advance-request
    // /payroll/advance-request/123 -> check /payroll/advance-request
    // /payroll/advance-request/create -> check /payroll/advance-request
    // /payroll/application-form/APP-001/edit -> check /payroll/application-form
    if (pathParts.length >= 3) {
      // Pattern to detect likely dynamic IDs:
      // - UUIDs (36 chars with dashes)
      // - Numeric IDs (e.g., 123)
      // - Alphanumeric IDs (20+ chars like cuid)
      // - IDs with prefixes like APP-001, EMP-123, LN-456, etc.
      // - Any segment containing numbers (likely an ID)
      const isLikelyIdPattern = /^[0-9a-f-]{36}$|^\d+$|^[a-zA-Z0-9]{20,}$|^[A-Z]+-\d+$|^[a-z]+-\d+$|.*\d+.*/i;

      // Try to find the base menu path by removing action segments and IDs
      for (let i = pathParts.length - 1; i >= 2; i--) {
        const segment = pathParts[i].toLowerCase();
        const isActionSegment = actionSegments.includes(segment);
        const isLikelyId = isLikelyIdPattern.test(pathParts[i]);

        if (isActionSegment || isLikelyId) {
          // Check if the parent path (without this segment) is accessible
          const parentPath = '/' + pathParts.slice(0, i).join('/');
          if (accessiblePaths.includes(parentPath)) {
            return true;
          }
        }
      }

      // Also check if removing all trailing action segments and IDs gives an accessible path
      let basePath = pathParts.slice();
      while (basePath.length >= 2) {
        const lastSegment = basePath[basePath.length - 1].toLowerCase();
        const isActionSegment = actionSegments.includes(lastSegment);
        const isLikelyId = isLikelyIdPattern.test(basePath[basePath.length - 1]);

        if (isActionSegment || isLikelyId) {
          basePath.pop();
          const checkPath = '/' + basePath.join('/');
          if (accessiblePaths.includes(checkPath)) {
            return true;
          }
          // If we've stripped down to a single module segment (e.g. '/payroll')
          // and the user has any child access under it, allow.
          if (basePath.length === 1) {
            const hasAnyChildAccess = accessiblePaths.some(ap =>
              ap.startsWith(checkPath + '/')
            );
            if (hasAnyChildAccess) {
              return true;
            }
          }
        } else {
          break;
        }
      }
    }

    // Allow access to module landing pages if user has access to any child in that module
    // e.g., if user has /payroll/company, they can access /payroll (landing page)
    if (pathParts.length === 1) {
      // This is a top-level module path like /payroll or /users
      const modulePath = '/' + pathParts[0];
      const hasAnyChildAccess = accessiblePaths.some(ap =>
        ap.startsWith(modulePath + '/')
      );
      if (hasAnyChildAccess) {
        return true;
      }
    }

    return false;
  }, [accessiblePaths, isLoading, user]);

  const refreshPaths = useCallback(async () => {
    // Force refresh from API
    await refreshMenus();
    await fetchAccessiblePaths(true);
  }, [fetchAccessiblePaths]);

  return (
    <MenuAccessContext.Provider value={{ accessiblePaths, isLoading, hasAccess, refreshPaths }}>
      {children}
    </MenuAccessContext.Provider>
  );
}

export function useMenuAccess(): MenuAccessContextType {
  const context = useContext(MenuAccessContext);
  if (context === undefined) {
    throw new Error('useMenuAccess must be used within a MenuAccessProvider');
  }
  return context;
}
